// 🧭 EPLSectionHeader - Componente de encabezado universal EPL
// 📋 Implementa Section 4 de EPL_UX_UI_POLICY.md

import React from 'react';
import { getPhaseColors, getPhaseGradient } from '../config/eplColors';

/**
 * Encabezado modular EPL que implementa la jerarquía estructural
 * Plano 1: Encabezado (Contexto) - Introducir el propósito del paso
 */
const EPLSectionHeader = ({ 
  variant = 'hero',    // Nueva prop: 'hero' o 'plain'
  phase,              // Clave de fase: 'contexto', 'hipotesis', etc.
  title,              // Título principal del paso
  subtitle,           // Subtítulo con objetivo o propósito
  icon: Icon,         // Componente de ícono Lucide
  progress = null,    // Porcentaje de progreso (opcional)
  currentStep = null, // Paso actual (opcional)
  totalSteps = null   // Total de pasos (opcional)
}) => {
  const colors = getPhaseColors(phase);
  const gradientClass = getPhaseGradient(phase);

  // Variante plain para pasos internos (sin fondo coloreado)
  if (variant === 'plain') {
    return (
      <header className="w-full bg-transparent px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{title}</h1>
        <p className="mt-1 text-sm sm:text-base text-gray-600">{subtitle}</p>
      </header>
    );
  }

  // Variante hero original (con fondo coloreado/gradiente)
  return (
    <div className={`${gradientClass} text-white p-6 mb-6 rounded-t-xl`}>
      <div className="flex items-center justify-between">
        {/* Lado izquierdo: Título + Subtítulo */}
        <div className="flex items-center gap-4">
          {Icon && (
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Icon className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">
              {title}
            </h2>
            <p className="text-white text-opacity-90 text-sm">
              {subtitle} {colors.cognitive && `• ${colors.cognitive}`}
            </p>
          </div>
        </div>

        {/* Lado derecho: Progreso */}
        {(progress !== null || currentStep !== null) && (
          <div className="text-right">
            {progress !== null && (
              <div className="text-2xl font-bold text-white mb-1">
                {progress}%
              </div>
            )}
            {currentStep !== null && totalSteps !== null && (
              <div className="text-white text-opacity-90 text-sm">
                Paso {currentStep} de {totalSteps}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Barra de progreso visual (si se proporciona) */}
      {progress !== null && (
        <div className="mt-4">
          <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EPLSectionHeader;